/**
 * Pick the board's density from the height it actually has — "fill the screen, no scrolling".
 * It measures one rendered row and header out of the DOM and scales the other tiers by {@link RATIO};
 * why predicting the heights instead failed is in `design-notes.md`.
 *
 * **Not a §4.8 violation:** nothing here writes a font size or any other style. The measured numbers
 * choose one of four prebuilt Tailwind class sets and never become CSS.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

import type { Tier } from './columns';

/** Row height of each tier *relative to* `lg`. Only the proportions matter; the scale is measured. */
const RATIO: Record<Tier, number> = { xl: 1.6, lg: 1, md: 0.63, sm: 0.49, desk: 1 };

/** Largest first — the loop takes the first that fits. */
const ORDER: readonly Tier[] = ['xl', 'lg', 'md', 'sm'];

/** Slack, so a board chosen to fit by three pixels does not overflow when a status pill wraps. */
const SAFETY_PX = 8;

/**
 * What a row would be tall if it were not being stretched: the table is `h-full`, so a measured
 * `<tr>` includes slack the browser handed it. Measures the tallest cell's content plus its padding.
 */
function naturalRowHeight(row: Element): number {
  let tallest = 0;
  for (const cell of row.children) {
    const content = cell.firstElementChild?.getBoundingClientRect().height ?? 0;
    const style = window.getComputedStyle(cell);
    const padded =
      content + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
      + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    tallest = Math.max(tallest, padded);
  }
  return tallest;
}

export interface FittedTier {
  /** Put this on the element that owns the height available to the table. */
  ref: RefObject<HTMLDivElement | null>;
  tier: Tier;
}

export function useFittedTier(kiosk: boolean, rowCount: number): FittedTier {
  const ref = useRef<HTMLDivElement>(null);
  // `md`, not `xl`: the first paint precedes any measurement, so the correction grows the board.
  const [tier, setTier] = useState<Tier>('md');

  useEffect(() => {
    const element = ref.current;
    if (!kiosk || element === null || rowCount === 0) {
      return;
    }

    // Never measure synchronously in the effect body: ResizeObserver fires once on observe(), so the
    // first measurement arrives here without a cascading render. `tier` is a dependency so it re-measures.
    const observer = new ResizeObserver(() => {
      const available = element.clientHeight;
      const row = element.querySelector('tbody tr');
      const header = element.querySelector('thead');
      if (available <= 0 || row === null || header === null) {
        return;
      }

      const naturalRow = naturalRowHeight(row);
      const headerHeight = header.getBoundingClientRect().height;
      if (naturalRow <= 0) {
        return;
      }

      // What one "ratio unit" of row costs, calibrated from the tier the closure holds — the one on screen.
      const unit = naturalRow / RATIO[tier];
      const fits = (candidate: Tier) =>
        headerHeight + unit * RATIO[candidate] * rowCount + SAFETY_PX <= available;

      setTier(ORDER.find(fits) ?? 'sm');
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [kiosk, rowCount, tier]);

  return { ref, tier: kiosk ? tier : 'desk' };
}
